import React, { useState, useRef, useEffect } from 'react';
import './SignupLoginModal.scss';
import { getCookie } from '../utils/utils';
import LoginOrSignup from './LoginOrSignup';
import Logout from './Logout';
import Login from './Login';
import Signup from './Signup';
import { CloseIcon } from '../common/CloseIcon';
import { Trans, useTranslation } from 'react-i18next';
import ForgotPassword from './ForgotPassword';

type Props = {
  toggleSignupLoginModalVisible: () => void;
  setIsLoggedIn: (isLoggedIn: boolean) => void;
  isLoggedIn: boolean;
  onLoginSuccess: (expiresIn: number) => void; // Add this new prop
  isLandingPage?: boolean;
};

const SignupLoginModal = (props: Props) => {
  const {
    toggleSignupLoginModalVisible,
    setIsLoggedIn,
    isLoggedIn,
    onLoginSuccess,
    isLandingPage,
  } = props;
  const { t, i18n } = useTranslation();
  const [email, setEmail] = useState('');
  const [showLoginView, setShowLoginView] = useState(false);
  const [showSignupView, setShowSignupView] = useState(false);
  const [showForgotPasswordView, setShowForgotPasswordView] = useState(false);
  const [
    showEmailVerificationCodeInputView,
    setShowEmailVerificationCodeInputView,
  ] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [loginError, setLoginError] = useState(''); // Add this line
  const [isVerifyingEmail, setIsVerifyingEmail] = useState(false);
  const [verificationError, setVerificationError] = useState(''); // Add this line

  const verificationCodeRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (showEmailVerificationCodeInputView && verificationCodeRef.current) {
      verificationCodeRef.current.focus();
    }
  }, [showEmailVerificationCodeInputView]);

  const handleForgotPasswordClick = () => {
    setShowLoginView(false);
    setShowForgotPasswordView(true);
    setLoginError('');
  };

  const validatePassword = (password: string) => {
    if (password.length < 8) {
      return t('signupLoginModal.passwordRequirements.length');
    }
    if (!/[A-Z]/.test(password)) {
      return t('signupLoginModal.passwordRequirements.uppercase');
    }
    if (!/[a-z]/.test(password)) {
      return t('signupLoginModal.passwordRequirements.lowercase');
    }
    if (!/[0-9]/.test(password)) {
      return t('signupLoginModal.passwordRequirements.number');
    }
    return '';
  };

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email) ? '' : 'Virheellinen sähköpostiosoite.';
  };

  const handleSubmitEmailVerificationCode = async () => {
    setIsVerifyingEmail(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/confirm-email`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': getCookie('csrf_access_token'),
          },
          body: JSON.stringify({ 
            email: email.toLowerCase(), 
            verification_code: verificationCode,
            language: i18n.language 
          }),
          credentials: 'include',
        },
      );

      if (response.ok) {
        const data = await response.json();
        setIsLoggedIn(true);
        onLoginSuccess(data.expires_in);
        toggleSignupLoginModalVisible();
      } else {
        const errorData = await response.json();
        if (errorData.error === 'invalid_code') {
          setVerificationError('Invalid verification code. Please try again.'); // Set error message
        } else {
          console.error('Email confirmation code failed');
        }
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setIsVerifyingEmail(false);
    }
  };

  return (
    <div className={`signup-login-modal-overlay ${isLandingPage ? 'signup-login-modal-overlay--landing-page' : ''}`}>
      <div
        className={`signup-login-modal ${
          showSignupView ? 'signup-login-modal--signup' : ''
        } ${
          showEmailVerificationCodeInputView
            ? 'signup-login-modal--email-verification'
            : ''
        } ${isLoggedIn ? 'signup-login-modal--logout-view' : ''} ${
          !showLoginView &&
          !showSignupView &&
          !showForgotPasswordView &&
          !showEmailVerificationCodeInputView &&
          !isLoggedIn
            ? 'signup-login-modal--login'
            : ''
        } ${
          showLoginView ||
          showSignupView ||
          showForgotPasswordView ||
          showEmailVerificationCodeInputView ||
          isLoggedIn
            ? ' signup-login-modal--no-info-box-view'
            : ''
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="signup-login-modal__close-wrapper">
          <button
            onClick={toggleSignupLoginModalVisible}
            className="signup-login-modal__close-wrapper__close-button"
          >
            <CloseIcon size={20} />
          </button>
        </div>
        {!showLoginView &&
          !showSignupView &&
          !showForgotPasswordView &&
          !showEmailVerificationCodeInputView &&
          !isLoggedIn && (
            <LoginOrSignup
              email={email}
              toggleSignupLoginModalVisible={toggleSignupLoginModalVisible}
              setShowLoginView={setShowLoginView}
              setShowSignupView={setShowSignupView}
              validateEmail={validateEmail}
              onLoginSuccess={onLoginSuccess}
              setIsLoggedIn={setIsLoggedIn}
              setEmail={setEmail}
            />
          )}
        {isLoggedIn && (
          <Logout
            toggleSignupLoginModalVisible={toggleSignupLoginModalVisible}
            setIsLoggedIn={setIsLoggedIn}
          />
        )}
        {showLoginView && (
          <Login
            email={email}
            loginError={loginError}
            toggleSignupLoginModalVisible={toggleSignupLoginModalVisible}
            onLoginSuccess={onLoginSuccess}
            setLoginError={setLoginError}
            validatePassword={validatePassword}
            onForgotPasswordClick={handleForgotPasswordClick}
          />
        )}
        {showSignupView && (
          <Signup
            email={email}
            setEmail={setEmail}
            setShowSignupView={setShowSignupView}
            setShowLoginView={setShowLoginView}
            validatePassword={validatePassword}
            setShowEmailVerificationCodeInputView={
              setShowEmailVerificationCodeInputView
            }
            setLoginError={setLoginError}
            validateEmail={validateEmail}
          />
        )}
        {showForgotPasswordView && (
          <ForgotPassword
            email={email}
            setEmail={setEmail}
            validateEmail={validateEmail}
            setShowForgotPasswordView={setShowForgotPasswordView}
            setShowLoginView={setShowLoginView}
          />
        )}
        {showEmailVerificationCodeInputView && (
          <>
            <div className="signup-login-modal__content">
              <div className="signup-login-modal__content__title">
                <Trans i18nKey="signupLoginModal.emailVerification.title" />
              </div>
            </div>
            <div className="signup-login-modal__content__subtitle">
              <div>
                <Trans i18nKey="signupLoginModal.emailVerification.firstParagraph" />
              </div>
              <div>
                <Trans
                  i18nKey="signupLoginModal.emailVerification.secondParagraph"
                  values={{ email }}
                />
              </div>
            </div>
            <form
              className="signup-login-modal__content__form"
              onSubmit={(e) => {
                e.preventDefault();
                handleSubmitEmailVerificationCode();
              }}
            >
              <input
                type="text"
                placeholder={t('signupLoginModal.emailVerification.codePlaceholder')}
                className="signup-login-modal__content__input"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                required
                ref={verificationCodeRef}
              />
              {verificationError && ( // Add this block
                <div className="signup-login-modal__content__error">
                  {verificationError}
                </div>
              )}
              <button
                className="signup-login-modal__content__button"
                disabled={isVerifyingEmail}
                type="submit"
              >
                {isVerifyingEmail ? (
                  <span className="loading-dots">
                    <span></span>
                  </span>
                ) : (
                  'Seuraava'
                )}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default SignupLoginModal;
